import { DEFAULT_DISCOUNT_RATE } from './discountHelpers.js';
import { calculatePricing } from './pricingEngine.js';
import { DELIVERY_PER_50_UNITS, extractVatFromGross, roundMoney } from './pricingConstants.js';

// Each code owns its exact scope and pricing rule. A coupon never changes the
// catalog price or the stored color/size matrices.
export const COUPON_RULES = Object.freeze({
  kfar2026: Object.freeze({
    type: 'fixed_unit_gross',
    freeDelivery: true,
    unitPriceInclVat: Object.freeze({ tshirt: 15, sweatshirt: 35, hoodie: 42 }),
    requiredPrintAreas: Object.freeze({
      tshirt: Object.freeze(['leftChest', 'backA4']),
      sweatshirt: Object.freeze(['leftChest_long', 'backA4_long']),
      hoodie: Object.freeze(['leftChest_long', 'backA4_long']),
    }),
  }),
  test99: Object.freeze({
    type: 'order_percent',
    discountRate: 0.99,
    activateExistingOffer: true,
    freeDelivery: false,
  }),
});

export function cartItemQuantity(item) {
  if (item?.sizeMatrices && typeof item.sizeMatrices === 'object') {
    return Object.values(item.sizeMatrices).reduce((sum, matrix) =>
      sum + Object.values(matrix || {}).reduce((part, qty) => part + (Number(qty) || 0), 0), 0);
  }
  if (item?.sizeMatrix && typeof item.sizeMatrix === 'object') {
    return Object.values(item.sizeMatrix).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
  }
  return Number(item?.quantity) || 0;
}

function couponLine(item, rule) {
  const sku = item?.productSku;
  const unitPriceInclVat = Object.hasOwn(rule.unitPriceInclVat, sku) ? rule.unitPriceInclVat[sku] : null;
  const requiredAreas = Object.hasOwn(rule.requiredPrintAreas, sku) ? rule.requiredPrintAreas[sku] : null;
  const quantity = cartItemQuantity(item);
  const selections = item?.selectedPrintAreas;
  if (!unitPriceInclVat || !requiredAreas || !Number.isInteger(quantity) || quantity <= 0 || !Array.isArray(selections)) {
    return null;
  }
  // A sum can look valid even when one color/size cell is fractional or
  // negative. Validate each stored variant quantity before using the offer.
  const matrices = item?.sizeMatrices && typeof item.sizeMatrices === 'object'
    ? Object.values(item.sizeMatrices)
    : [item?.sizeMatrix];
  const quantities = matrices.flatMap((matrix) => matrix && typeof matrix === 'object' ? Object.values(matrix) : []);
  if (quantities.length > 0 && quantities.some((value) => !Number.isInteger(Number(value)) || Number(value) < 0)) {
    return null;
  }

  if (!hasRequiredPrintAreas(item, rule)) return null;

  return { quantity, unitPriceInclVat, totalInclVat: roundMoney(quantity * unitPriceInclVat) };
}

function hasRequiredPrintAreas(item, rule) {
  const sku = item?.productSku;
  const requiredAreas = Object.hasOwn(rule.requiredPrintAreas, sku) ? rule.requiredPrintAreas[sku] : null;
  const selections = item?.selectedPrintAreas;
  if (!requiredAreas || !Array.isArray(selections) || selections.length !== requiredAreas.length) return false;
  const selectedKeys = new Set();
  for (const entry of selections) {
    const selection = typeof entry === 'string' ? { areaKey: entry, method: 'print' } : entry;
    if (selection?.method !== 'print' || !requiredAreas.includes(selection?.areaKey)) return false;
    selectedKeys.add(selection.areaKey);
  }
  return selectedKeys.size === requiredAreas.length;
}

export function evaluateCoupon(code, cartItems) {
  const normalizedCode = typeof code === 'string' ? code.trim() : '';
  if (!normalizedCode) return { status: 'none', code: '', eligibleLines: new Map(), invalidAreas: false };
  const rule = Object.hasOwn(COUPON_RULES, normalizedCode) ? COUPON_RULES[normalizedCode] : null;
  if (!rule) return { status: 'unknown_code', code: normalizedCode, eligibleLines: new Map(), invalidAreas: false };

  if (rule.type === 'order_percent') {
    const hasItems = (Array.isArray(cartItems) ? cartItems : []).some((item) => cartItemQuantity(item) > 0);
    return {
      status: hasItems ? 'applied' : 'no_eligible_items',
      code: normalizedCode,
      type: rule.type,
      discountRate: rule.discountRate,
      activateExistingOffer: rule.activateExistingOffer,
      freeDelivery: rule.freeDelivery,
      eligibleLines: new Map(),
      invalidAreas: false,
    };
  }

  const eligibleLines = new Map();
  let invalidAreas = false;
  (Array.isArray(cartItems) ? cartItems : []).forEach((item, index) => {
    if (Object.hasOwn(rule.requiredPrintAreas, item?.productSku) && !hasRequiredPrintAreas(item, rule)) {
      invalidAreas = true;
    }
    const line = couponLine(item, rule);
    if (line) eligibleLines.set(index, line);
  });
  return {
    status: eligibleLines.size ? 'applied' : invalidAreas ? 'invalid_areas' : 'no_eligible_items',
    code: normalizedCode,
    type: rule.type,
    freeDelivery: rule.freeDelivery,
    eligibleLines,
    invalidAreas,
  };
}

export function priceCurrentCatalogItem(item, lineIndex = 0) {
  const matrices = item?.sizeMatrices && typeof item.sizeMatrices === 'object' && !Array.isArray(item.sizeMatrices)
    ? item.sizeMatrices
    : item?.sizeMatrix && typeof item.sizeMatrix === 'object' && !Array.isArray(item.sizeMatrix)
      ? { [String(item.color || item.selectedColor || 'unspecified')]: item.sizeMatrix }
      : null;
  if (!matrices || !Array.isArray(item?.selectedPrintAreas)) {
    return { ok: false, error: { lineIndex, code: 'missing_price_dimensions' } };
  }
  if (item.selectedPrintAreas.some((selection) => !selection || (typeof selection !== 'string' && (
    typeof selection !== 'object' || !selection.areaKey || !['print', 'embo', undefined].includes(selection.method)
  )))) {
    return { ok: false, error: { lineIndex, code: 'invalid_print_area' } };
  }
  const quantities = Object.values(matrices).flatMap((matrix) =>
    matrix && typeof matrix === 'object' && !Array.isArray(matrix) ? Object.values(matrix) : [NaN]);
  if (!quantities.length || quantities.some((quantity) =>
    quantity === null || quantity === '' || typeof quantity === 'boolean'
    || !Number.isInteger(Number(quantity)) || Number(quantity) < 0)) {
    return { ok: false, error: { lineIndex, code: 'invalid_quantity' } };
  }
  const pricing = calculatePricing({
    sku: item.productSku,
    sizeMatrices: matrices,
    selectedPrintAreas: item.selectedPrintAreas,
    withDelivery: false,
    discountClaimed: false,
  });
  if (!pricing.ok || pricing.totalQty <= 0) {
    return {
      ok: false,
      error: { lineIndex, code: pricing.errors?.[0]?.code || pricing.errors?.[0] || 'unpriceable_item' },
    };
  }
  return { ok: true, pricing };
}

export const GROSS_TARIFF_VERSION = 'gross-v1-2026-09-25';

// Reprice each saved row from its exact SKU, matrices and placements. Monetary
// fields from old localStorage carts were pre-VAT and must never be reused.
export function repriceCartItem(item, lineIndex = 0) {
  const {
    couponCode: _oldCouponCode,
    couponUnitPriceInclVat: _oldCouponUnitPrice,
    couponVatAmount: _oldCouponVat,
    ...identity
  } = item || {};
  const current = priceCurrentCatalogItem(item, lineIndex);
  if (!current.ok) {
    return {
      ...identity,
      pricingVersion: GROSS_TARIFF_VERSION,
      pricingError: current.error,
      discountClaimed: item?.discountClaimed === true,
      unitPrice: null,
      totalPrice: null,
      totalIls: null,
      totalBeforeDiscount: null,
      discountAmount: null,
      subtotalBeforeDiscount: null,
      subtotalAfterDiscount: null,
      priceBreakdown: null,
      vatBase: null,
      vatAmount: null,
      deliveryCost: null,
      baseTotal: null,
      paymentTotal: null,
    };
  }
  const { pricing } = current;
  const subtotalBeforeDiscount = roundMoney(pricing.subtotalBeforeDiscount);
  const discountClaimed = item.discountClaimed === true;
  const discountAmount = discountClaimed ? roundMoney(subtotalBeforeDiscount * DEFAULT_DISCOUNT_RATE) : 0;
  const subtotalAfterDiscount = roundMoney(subtotalBeforeDiscount - discountAmount);
  const unitPrice = pricing.breakdown.emboUnitsCount > 0
    ? null
    : roundMoney(pricing.breakdown.unitBase + pricing.breakdown.placementFeesPerUnit);
  const { net: vatBase, vat: vatAmount } = extractVatFromGross(subtotalAfterDiscount);
  return {
    ...identity,
    pricingVersion: GROSS_TARIFF_VERSION,
    pricingError: null,
    discountClaimed,
    unitPrice,
    totalPrice: subtotalAfterDiscount,
    totalIls: subtotalAfterDiscount,
    totalBeforeDiscount: subtotalBeforeDiscount,
    discountAmount,
    discountRate: discountClaimed ? DEFAULT_DISCOUNT_RATE : 0,
    subtotalBeforeDiscount,
    subtotalAfterDiscount,
    vatBase,
    vatAmount,
    deliveryCost: 0,
    baseTotal: subtotalAfterDiscount,
    paymentTotal: subtotalAfterDiscount,
    priceBreakdown: {
      ...pricing.breakdown,
      discountAmount,
      discountRate: discountClaimed ? DEFAULT_DISCOUNT_RATE : 0,
      subtotalAfterDiscount,
      totalAfterDiscount: subtotalAfterDiscount,
    },
  };
}

export function priceCart({ cartItems = [], couponCode = '', discountClaimed = false, withDelivery = false, paymentMethod = '' } = {}) {
  const items = Array.isArray(cartItems) ? cartItems : [];
  const coupon = evaluateCoupon(couponCode, items);
  const couponApplied = coupon.status === 'applied';
  const fixedUnitCouponApplied = couponApplied && coupon.type === 'fixed_unit_gross';
  const existingOfferActive = discountClaimed || (couponApplied && coupon.activateExistingOffer);
  const errors = [];
  const lines = items.map((item, index) => {
    const current = priceCurrentCatalogItem(item, index);
    if (!current.ok) {
      errors.push(current.error);
      return { item, priceError: current.error, couponApplied: false, couponUnitPriceInclVat: null };
    }
    const subtotalBefore = roundMoney(current.pricing.subtotalBeforeDiscount);
    const eligible = fixedUnitCouponApplied ? coupon.eligibleLines.get(index) : null;
    const standardDiscount = !fixedUnitCouponApplied && (
      existingOfferActive || item.discountClaimed === true
    );
    const discountRate = standardDiscount ? DEFAULT_DISCOUNT_RATE : 0;
    const subtotalAfter = eligible
      ? eligible.totalInclVat
      : fixedUnitCouponApplied
        ? subtotalBefore
        : roundMoney(subtotalBefore - roundMoney(subtotalBefore * discountRate));
    return {
      item,
      subtotalBefore,
      subtotalAfter,
      discountAmount: roundMoney(subtotalBefore - subtotalAfter),
      discountRate,
      discountApplied: subtotalBefore > subtotalAfter,
      couponApplied: Boolean(eligible),
      couponUnitPriceInclVat: eligible ? eligible.unitPriceInclVat : null,
      breakdown: current.pricing.breakdown,
      quantity: current.pricing.totalQty,
    };
  });

  if (errors.length) {
    return {
      ok: false,
      errors,
      priceIncludesVat: true,
      coupon,
      couponApplied,
      lines,
      subtotalBeforeDiscount: null,
      subtotalAfterDiscount: null,
      discountAmount: null,
      deliveryCost: null,
      vatBase: null,
      vatAmount: null,
      baseTotal: null,
      cardDiscount: null,
      couponDiscount: null,
      paymentBeforeCoupon: null,
      paymentTotal: null,
    };
  }

  const subtotalBeforeDiscount = roundMoney(lines.reduce((sum, line) => sum + line.subtotalBefore, 0));
  const subtotalAfterDiscount = roundMoney(lines.reduce((sum, line) => sum + line.subtotalAfter, 0));
  const discountAmount = roundMoney(subtotalBeforeDiscount - subtotalAfterDiscount);
  const totalQuantity = lines.reduce((sum, line) => sum + line.quantity, 0);
  const deliveryCost = withDelivery && !(couponApplied && coupon.freeDelivery)
    ? Math.ceil(totalQuantity / 50) * DELIVERY_PER_50_UNITS : 0;
  const baseTotal = roundMoney(subtotalAfterDiscount + deliveryCost);
  const cardDiscount = paymentMethod === 'card' && !fixedUnitCouponApplied ? Math.round(baseTotal * 0.03) : 0;
  const paymentBeforeCoupon = roundMoney(baseTotal - cardDiscount);
  const paymentTotal = couponApplied && coupon.type === 'order_percent'
    ? roundMoney(paymentBeforeCoupon * (1 - coupon.discountRate))
    : paymentBeforeCoupon;
  const couponDiscount = roundMoney(paymentBeforeCoupon - paymentTotal);
  const { net: vatBase, vat: vatAmount } = extractVatFromGross(paymentTotal);

  return {
    ok: true,
    errors: [],
    priceIncludesVat: true,
    coupon,
    couponApplied,
    lines,
    subtotalBeforeDiscount,
    subtotalAfterDiscount,
    discountAmount,
    deliveryCost,
    vatBase,
    vatAmount,
    baseTotal,
    cardDiscount,
    couponDiscount,
    paymentBeforeCoupon,
    paymentTotal,
  };
}
